import React, { useEffect, useState } from 'react';
import './reservedTimeGrid.scss';
import LanguageProvider from '@/providers/languageProvider';
import ReservationInsightsFilter from '../../interfaces/reservationInsightsFilter';
import { IHourPercentage, IWeekDayHourPercentage } from '../../interfaces/IWeekDayHourPercentage';
import HistoricReservationService from '../historicReservationService';
import ActuallyCenteredLoader from '@/components/loaders/actuallyCenteredLoader';

interface IReservedTimeGridProps {
    filter: ReservationInsightsFilter;
};

const ReservedTimeGrid: React.FC<IReservedTimeGridProps> = (props) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [reservedWeekDayHourPercentages, setReservedWeekDayHourPercentages] = useState<IWeekDayHourPercentage[]>([]);
    const [localFilter, setLocalFilter] = useState<ReservationInsightsFilter>(props.filter); // Local filter to avoid re-rendering when parent filter changes
    const [maxPercentage, setMaxPercentage] = useState<number>(100);

    const historicReservationService = new HistoricReservationService();

    useEffect(() => {
        setLocalFilter(props.filter);
    }, [props.filter.triggerUpdate]);

    useEffect(() => {
        setReservedWeekDayHourPercentages([]);
        setLoading(true);

        historicReservationService.getPlaceReservationsWeekDayHourPercentage(props.filter).then((response) => {
            setReservedWeekDayHourPercentages(response);
            getMaxAndMinPercentages(response);
            setLoading(false);
        });

    }, [localFilter]);

    function getMaxAndMinPercentages(response: IWeekDayHourPercentage[]): void {
        let maxPercentage = 0;

        response.forEach(dayData => {
            dayData.hourPercentages.forEach(hourData => {
                if (hourData.percentage > maxPercentage) {
                    maxPercentage = hourData.percentage;
                }
            });
        });

        setMaxPercentage(maxPercentage);
    };

    function calculateRowAverage(values: IHourPercentage[]): number {
        const sum = values.reduce((acc, value) => acc + value.percentage, 0);
        return Math.round(sum / values.length);
    }

    function calculateColumnAverages(start: number, end: number): number[] {
        if (reservedWeekDayHourPercentages.length === 0) {
            return [];
        }

        let rowCount = 0;

        if (!localFilter.weekdays || localFilter.weekdays.length === 0) {
            rowCount = 7;
        } else {
            rowCount = localFilter.weekdays.length;
        }

        const columnSums = Array(end - start).fill(0);

        for (let i = 0; i < columnSums.length; i++) {
            reservedWeekDayHourPercentages.forEach((dayData): void => {
                if (dayData.hourPercentages[i] && dayData.hourPercentages[i].percentage) {
                    columnSums[i] += dayData.hourPercentages[i].percentage;
                }
            });
        }
        return columnSums.map(sum => Math.round(sum / rowCount));
    }

    function getBackgroundColor(value: number): string {

        const maxColor = { r: 67, g: 189, b: 207 };

        const intensity = Math.floor((value / 100) * 100);

        const r = Math.floor(255 - (intensity / maxPercentage) * (255 - maxColor.r));
        const g = Math.floor(255 - (intensity / maxPercentage) * (255 - maxColor.g));
        const b = Math.floor(255 - (intensity / maxPercentage) * (255 - maxColor.b));

        return `rgb(${r}, ${g}, ${b})`;
    }

    const columnAverages = calculateColumnAverages(localFilter.startHourOfTheDay || 0, localFilter.endHourOfTheDay || 24);

    return (
        <div className="reserved-time-grid-container">
            <div className="widget-title">{LanguageProvider.getTranslation('pages.realestateutilization.charts.reservedtimegrid.title')}</div>
            {reservedWeekDayHourPercentages && reservedWeekDayHourPercentages.length > 0 && reservedWeekDayHourPercentages[0].hourPercentages &&
                <table>
                    <thead>
                        <tr>
                            <th className="standard-font font-weight-bold">{LanguageProvider.getTranslation('pages.realestateutilization.charts.reservedtimegrid.columnrowlabel')}</th>
                            {reservedWeekDayHourPercentages[0].hourPercentages.map((value) => (
                                <th className="standard-font" key={value.hour}>{`${value.hour}-${value.hour + 1}`}</th>
                            ))}
                            <th>{LanguageProvider.getTranslation('pages.realestateutilization.charts.reservedtimegrid.average')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reservedWeekDayHourPercentages.map((weekdayhourpercentage, index) => {

                            const rowAverage = calculateRowAverage(weekdayhourpercentage.hourPercentages);

                            return (
                                <tr key={index}>
                                    <td>{LanguageProvider.getTranslation(`pages.settings.workspaces.generaltab.weekdays.weekday${weekdayhourpercentage.weekday}`)}</td>
                                    {weekdayhourpercentage.hourPercentages.map((value, index) => {
                                        const backgroundColor = getBackgroundColor(value.percentage);
                                        return (<td key={index} style={{ backgroundColor }}>{Math.round(value.percentage)}%</td>);
                                    })}
                                    <td style={{ textAlign: 'center' }}>{rowAverage}%</td>
                                </tr>
                            );
                        })}
                        <tr>
                            <td>{LanguageProvider.getTranslation('pages.realestateutilization.charts.reservedtimegrid.average')}</td>
                            {columnAverages.map((average, index) => (
                                <td key={index}>{average}%</td>
                            ))}
                            <td></td>
                        </tr>
                    </tbody>
                </table>}
            {loading && <ActuallyCenteredLoader height="70%" />}
        </div>
    );
};

export default ReservedTimeGrid;
